import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axiosInstance from '../../api/commonAPI';
import { uploadProductImage } from '../../api/productImage';
import ProductImage from '../../components/ProductImage';

interface Product {
    id: number;
    name: string;
    price: number;
    description: string;
    imageUrl: string | null;
    onDisplay: boolean;
    soldOut: boolean;
    sortOrder: number;
    categoryId: number | null;
    categoryName: string | null;
}

interface Category {
    id: number;
    name: string;
}

const ProductManagementPage = () => {
    const navigate = useNavigate();
    const { storeId } = useParams<{ storeId: string }>();

    const [products, setProducts] = useState<Product[]>([]);
    const [categories, setCategories] = useState<Category[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [editingProduct, setEditingProduct] = useState<Product | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [editError, setEditError] = useState<string | null>(null);
    const [editImage, setEditImage] = useState<File | null>(null);
    const [removeImage, setRemoveImage] = useState(false);
    const [imagePreview, setImagePreview] = useState<string | null>(null);

    useEffect(() => {
        if (!editImage) { setImagePreview(null); return; }
        const url = URL.createObjectURL(editImage);
        setImagePreview(url);
        return () => URL.revokeObjectURL(url);
    }, [editImage]);

    const handleSaveProduct = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!editingProduct || isSaving) return;
        if (!editingProduct.name.trim() || !Number.isInteger(editingProduct.price) || editingProduct.price <= 0 || editingProduct.price > 2147483647) {
            setEditError('상품명과 올바른 가격을 입력해 주세요.');
            return;
        }
        setIsSaving(true);
        setEditError(null);
        try {
            const photo = editImage ? await uploadProductImage(editImage) : undefined;
            const response = await axiosInstance.patch<Product>(`/api/stores/${storeId}/admin/products/${editingProduct.id}`, {
                name: editingProduct.name.trim(),
                price: editingProduct.price,
                description: editingProduct.description,
                categoryId: editingProduct.categoryId,
                ...(photo ? { photo } : {}),
                ...(removeImage ? { removeImage: true } : {}),
            });
            setProducts(previous => previous.map(product => product.id === editingProduct.id ? response.data : product));
            setEditingProduct(null);
        } catch {
            setEditError('상품을 수정하지 못했습니다. 다시 시도해 주세요.');
        } finally {
            setIsSaving(false);
        }
    };

    useEffect(() => {
        if (!storeId) return;

        setIsLoading(true);
        Promise.all([
            axiosInstance.get<Product[]>(`/api/stores/${storeId}/admin/products`),
            axiosInstance.get<Category[]>(`/api/stores/${storeId}/categories`),
        ])
            .then(([productResponse, categoryResponse]) => {
                setProducts(productResponse.data);
                setCategories(categoryResponse.data);
                setError(null);
            })
            .catch(err => {
                console.error('상품 목록 조회 실패:', err);
                setError('상품 목록을 불러오지 못했습니다.');
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [storeId]);

    const handleGoBack = () => {
        navigate('/');
    };

    const handleGoToProductCreate = () => {
        navigate(`/admin/stores/${storeId}/products/new`);
    };

    const handleToggleDisplay = async (productId: number, currentStatus: boolean) => {
        setProducts(prevProducts =>
            prevProducts.map(product =>
                product.id === productId ? { ...product, onDisplay: !currentStatus } : product
            )
        );

        try {
            await axiosInstance.patch(`/api/stores/${storeId}/admin/products/${productId}/display`, {
                onDisplay: !currentStatus
            });
        } catch (error) {
            console.error('상품 판매 상태 변경 실패:', error);
            alert('상품 판매 상태를 변경하지 못했습니다.');
            setProducts(prevProducts =>
                prevProducts.map(product =>
                    product.id === productId ? { ...product, onDisplay: currentStatus } : product
                )
            );
        }
    };

    const handleToggleSoldOut = async (productId: number, currentStatus: boolean) => {
        setProducts(prevProducts =>
            prevProducts.map(product =>
                product.id === productId ? { ...product, soldOut: !currentStatus } : product
            )
        );

        try {
            await axiosInstance.patch(`/api/stores/${storeId}/admin/products/${productId}/sold-out`, {
                soldOut: !currentStatus
            });
        } catch (error) {
            console.error('상품 품절 상태 변경 실패:', error);
            alert('상품 품절 상태를 변경하지 못했습니다.');
            setProducts(prevProducts =>
                prevProducts.map(product =>
                    product.id === productId ? { ...product, soldOut: currentStatus } : product
                )
            );
        }
    };

    const handleDeleteProduct = async (productId: number, productName: string) => {
        if (!storeId) return;
        if (!window.confirm(`'${productName}' 상품을 제거할까요?`)) return;

        try {
            await axiosInstance.delete(`/api/stores/${storeId}/admin/products/${productId}`);
            setProducts(prevProducts => prevProducts.filter(product => product.id !== productId));
        } catch (error) {
            console.error('상품 제거 실패:', error);
            alert('상품을 제거하지 못했습니다. 이미 주문 이력에 포함된 상품이면 판매중지로 관리해 주세요.');
        }
    };

    const handleMoveProduct = async (productId: number, groupProducts: Product[], direction: -1 | 1) => {
        if (!storeId) return;

        const currentGroupIndex = groupProducts.findIndex(product => product.id === productId);
        const targetGroupIndex = currentGroupIndex + direction;
        if (currentGroupIndex < 0 || targetGroupIndex < 0 || targetGroupIndex >= groupProducts.length) return;

        const targetProductId = groupProducts[targetGroupIndex].id;
        const currentIndex = products.findIndex(product => product.id === productId);
        const targetIndex = products.findIndex(product => product.id === targetProductId);
        if (currentIndex < 0 || targetIndex < 0) return;

        const previousProducts = [...products];
        const nextProducts = [...products];
        [nextProducts[currentIndex], nextProducts[targetIndex]] = [nextProducts[targetIndex], nextProducts[currentIndex]];
        setProducts(nextProducts);

        try {
            await axiosInstance.patch(`/api/stores/${storeId}/admin/products/order`, {
                ids: nextProducts.map(product => product.id),
            });
        } catch (error) {
            console.error('상품 정렬 변경 실패:', error);
            alert('상품 순서를 변경하지 못했습니다.');
            setProducts(previousProducts);
        }
    };

    const categorizedSections = categories.map(category => ({
        id: category.id,
        name: category.name,
        products: products.filter(product => product.categoryId === category.id),
    }));

    const uncategorizedProducts = products.filter(product => !product.categoryId);
    const productSections = [
        ...categorizedSections,
        ...(uncategorizedProducts.length > 0 || categories.length === 0
            ? [{ id: 0, name: '카테고리 없음', products: uncategorizedProducts.length > 0 ? uncategorizedProducts : products }]
            : []),
    ];

    return (
        <div style={containerStyle}>
            {editingProduct && (
                <div style={modalBackdropStyle}>
                    <div role="dialog" aria-modal="true" aria-labelledby="product-edit-title" style={editModalStyle}
                        onKeyDown={event => { if (event.key === 'Escape' && !isSaving) setEditingProduct(null); }}>
                        <h2 id="product-edit-title">상품 수정</h2>
                        <form onSubmit={handleSaveProduct}>
                            <fieldset disabled={isSaving} style={{ border: 0, padding: 0, margin: 0 }}>
                                <ProductImage loading="eager" src={imagePreview || (removeImage ? undefined : editingProduct.imageUrl || undefined)}
                                    alt="상품 이미지 미리보기" style={{ width: '100%', height: 180, objectFit: 'contain', marginBottom: 12 }} />
                                <label style={editLabelStyle}>상품 이미지
                                    <input type="file" accept="image/*" style={editInputStyle}
                                        key={`${editingProduct.id}-${removeImage}`}
                                        onChange={event => {
                                            const file = event.target.files?.[0];
                                            if (!file) return;
                                            if (!file.type.startsWith('image/')) {
                                                setEditError('이미지 파일을 선택해 주세요.');
                                                event.target.value = '';
                                                return;
                                            }
                                            setEditImage(file); setRemoveImage(false); setEditError(null);
                                        }} />
                                </label>
                                <button type="button" style={{ marginBottom: 16 }}
                                    onClick={() => { setEditImage(null); setRemoveImage(true); }}>기본 이미지 사용</button>
                                <label style={editLabelStyle}>상품명
                                    <input autoFocus required value={editingProduct.name} style={editInputStyle}
                                        onChange={event => setEditingProduct({ ...editingProduct, name: event.target.value })} />
                                </label>
                                <label style={editLabelStyle}>가격
                                    <input type="number" required min={1} max={2147483647} step={1} value={Number.isNaN(editingProduct.price) ? '' : editingProduct.price} style={editInputStyle}
                                        onChange={event => setEditingProduct({ ...editingProduct, price: event.target.valueAsNumber })} />
                                </label>
                                <label style={editLabelStyle}>카테고리
                                    <select value={editingProduct.categoryId ?? ''} style={editInputStyle}
                                        onChange={event => setEditingProduct({ ...editingProduct, categoryId: event.target.value ? Number(event.target.value) : null })}>
                                        <option value="">카테고리 없음</option>
                                        {categories.map(category => <option key={category.id} value={category.id}>{category.name}</option>)}
                                    </select>
                                </label>
                                <label style={editLabelStyle}>상품 설명
                                    <textarea rows={4} value={editingProduct.description ?? ''} style={editInputStyle}
                                        onChange={event => setEditingProduct({ ...editingProduct, description: event.target.value })} />
                                </label>
                                {editError && <p role="alert" style={{ color: '#b91c1c' }}>{editError}</p>}
                                <div style={{ display: 'flex', gap: 8 }}>
                                    <button type="button" onClick={() => setEditingProduct(null)} style={{ ...actionButtonStyle, backgroundColor: '#6c757d' }}>취소</button>
                                    <button type="submit" style={{ ...actionButtonStyle, backgroundColor: '#2563eb' }}>{isSaving ? '저장 중...' : '저장'}</button>
                                </div>
                            </fieldset>
                        </form>
                    </div>
                </div>
            )}
            <div style={headerStyle}>
                <button onClick={handleGoBack} style={backButtonStyle}>
                    &larr;
                </button>
                <h2 style={{ flexGrow: 1, margin: 0 }}>상품 관리</h2>
                <button onClick={handleGoToProductCreate} style={createButtonStyle}>
                    새 상품 등록
                </button>
            </div>

            <p style={{ color: '#666', marginTop: 0 }}>
                매장에서 판매할 상품을 확인하고 고객 주문 화면 노출 여부와 품절 상태를 관리합니다.
            </p>

            {isLoading ? (
                <p>상품 목록을 불러오는 중...</p>
            ) : error ? (
                <p style={{ color: 'red' }}>{error}</p>
            ) : products.length > 0 ? (
                <div style={sectionListStyle}>
                    {productSections.map(section => (
                        <section key={section.id} style={categorySectionStyle}>
                            <div style={categoryHeaderStyle}>
                                <h3 style={{ margin: 0 }}>{section.name}</h3>
                                <span style={categoryCountStyle}>{section.products.length}개</span>
                            </div>
                            {section.products.length > 0 ? (
                                <div style={gridStyle}>
                                    {section.products.map((product, index) => (
                                        <div
                                            key={product.id}
                                            style={{
                                                ...cardStyle,
                                                border: product.onDisplay && !product.soldOut ? '2px solid #007bff' : '1px solid #ddd',
                                                opacity: product.onDisplay ? 1 : 0.72
                                            }}
                                        >
                                            <div style={imageWrapStyle}>
                                                <ProductImage src={product.imageUrl || undefined} alt={product.name} style={imageStyle} />
                                                <div style={badgeRowStyle}>
                                                    {!product.onDisplay && <span style={stoppedBadgeStyle}>판매중지</span>}
                                                    {product.soldOut && <span style={soldOutBadgeStyle}>품절</span>}
                                                </div>
                                            </div>
                                            <div style={cardBodyStyle}>
                                                <div>
                                                    <div style={sortControlsStyle}>
                                                        <span style={sortLabelStyle}>카테고리 내 순서 {index + 1}</span>
                                                        <div style={sortButtonGroupStyle}>
                                                            <button
                                                                onClick={() => handleMoveProduct(product.id, section.products, -1)}
                                                                disabled={index === 0}
                                                                style={sortButtonStyle}
                                                                type="button"
                                                            >
                                                                위
                                                            </button>
                                                            <button
                                                                onClick={() => handleMoveProduct(product.id, section.products, 1)}
                                                                disabled={index === section.products.length - 1}
                                                                style={sortButtonStyle}
                                                                type="button"
                                                            >
                                                                아래
                                                            </button>
                                                        </div>
                                                    </div>
                                                    <h3 style={{ margin: '0 0 10px 0' }}>{product.name}</h3>
                                                    <p style={{ margin: '0 0 10px 0', color: '#555', fontWeight: 700 }}>
                                                        {product.price.toLocaleString()}원
                                                    </p>
                                                    <p
                                                        style={{
                                                            margin: 0,
                                                            color: '#777',
                                                            fontSize: '14px',
                                                            marginBottom: '15px'
                                                        }}
                                                    >
                                                        {product.description}
                                                    </p>
                                                </div>

                                                <div style={actionRowStyle}>
                                                    <button type="button"
                                                        onClick={() => { setEditingProduct({ ...product }); setEditError(null); setEditImage(null); setRemoveImage(false); }}
                                                        style={{ ...actionButtonStyle, backgroundColor: '#2563eb' }}>
                                                        수정
                                                    </button>
                                                    <button
                                                        onClick={() => handleToggleDisplay(product.id, product.onDisplay)}
                                                        style={{
                                                            ...actionButtonStyle,
                                                            backgroundColor: product.onDisplay ? '#6c757d' : '#28a745',
                                                        }}
                                                    >
                                                        {product.onDisplay ? '판매중지' : '판매재개'}
                                                    </button>
                                                    <button
                                                        onClick={() => handleToggleSoldOut(product.id, product.soldOut)}
                                                        style={{
                                                            ...actionButtonStyle,
                                                            backgroundColor: product.soldOut ? '#2563eb' : '#dc3545',
                                                        }}
                                                    >
                                                        {product.soldOut ? '품절 해제' : '품절 처리'}
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteProduct(product.id, product.name)}
                                                        style={{
                                                            ...actionButtonStyle,
                                                            backgroundColor: '#991b1b',
                                                        }}
                                                    >
                                                        제거
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p style={emptyCategoryStyle}>이 카테고리에 등록된 상품이 없습니다.</p>
                            )}
                        </section>
                    ))}
                </div>
            ) : (
                <p>등록된 상품이 없습니다. 새 상품을 등록해 주세요.</p>
            )}
        </div>
    );
};

const containerStyle: React.CSSProperties = {
    padding: '30px',
    fontFamily: 'sans-serif'
};

const headerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
    marginBottom: '20px',
    borderBottom: '1px solid #eee',
    paddingBottom: '20px'
};

const backButtonStyle: React.CSSProperties = {
    padding: '10px 15px',
    fontSize: '18px',
    lineHeight: 1,
    backgroundColor: '#6c757d',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer'
};

const createButtonStyle: React.CSSProperties = {
    padding: '10px 20px',
    fontSize: '16px',
    cursor: 'pointer',
    color: 'white',
    backgroundColor: '#007bff',
    border: 'none',
    borderRadius: '5px'
};

const sectionListStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '28px'
};

const categorySectionStyle: React.CSSProperties = {
    padding: '18px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    backgroundColor: '#fff'
};

const categoryHeaderStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    marginBottom: '16px',
    paddingBottom: '12px',
    borderBottom: '1px solid #f1f5f9'
};

const categoryCountStyle: React.CSSProperties = {
    padding: '4px 10px',
    borderRadius: '999px',
    backgroundColor: '#eff6ff',
    color: '#1d4ed8',
    fontSize: '13px',
    fontWeight: 800
};

const emptyCategoryStyle: React.CSSProperties = {
    margin: 0,
    padding: '24px',
    border: '1px dashed #cbd5e1',
    borderRadius: '8px',
    backgroundColor: '#f8fafc',
    color: '#64748b',
    textAlign: 'center',
    fontWeight: 700
};

const gridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
    gap: '20px'
};

const cardStyle: React.CSSProperties = {
    border: '1px solid #ddd',
    borderRadius: '8px',
    overflow: 'hidden',
    boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
    display: 'flex',
    flexDirection: 'column'
};

const imageWrapStyle: React.CSSProperties = {
    position: 'relative'
};

const imageStyle: React.CSSProperties = {
    width: '100%',
    height: '200px',
    objectFit: 'cover'
};

const badgeRowStyle: React.CSSProperties = {
    position: 'absolute',
    top: '10px',
    right: '10px',
    display: 'flex',
    gap: '6px',
    flexWrap: 'wrap',
    justifyContent: 'flex-end'
};

const stoppedBadgeStyle: React.CSSProperties = {
    padding: '4px 8px',
    borderRadius: '999px',
    backgroundColor: '#111827',
    color: '#fff',
    fontSize: '12px',
    fontWeight: 800
};

const soldOutBadgeStyle: React.CSSProperties = {
    padding: '4px 8px',
    borderRadius: '999px',
    backgroundColor: '#dc3545',
    color: '#fff',
    fontSize: '12px',
    fontWeight: 800
};

const cardBodyStyle: React.CSSProperties = {
    padding: '15px',
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between'
};

const actionRowStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '8px'
};

const sortControlsStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    marginBottom: '10px'
};

const sortLabelStyle: React.CSSProperties = {
    color: '#64748b',
    fontSize: '13px',
    fontWeight: 800
};

const sortButtonGroupStyle: React.CSSProperties = {
    display: 'flex',
    gap: '6px'
};

const sortButtonStyle: React.CSSProperties = {
    padding: '5px 9px',
    border: '1px solid #cbd5e1',
    borderRadius: '4px',
    backgroundColor: '#fff',
    color: '#334155',
    cursor: 'pointer',
    fontWeight: 700
};

const actionButtonStyle: React.CSSProperties = {
    width: '100%',
    padding: '8px',
    cursor: 'pointer',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontWeight: 700,
    transition: 'background-color 0.2s'
};

export default ProductManagementPage;

const modalBackdropStyle: React.CSSProperties = {
    position: 'fixed', inset: 0, zIndex: 10000, backgroundColor: 'rgba(15, 23, 42, 0.55)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
};
const editModalStyle: React.CSSProperties = {
    width: '100%', maxWidth: 480, maxHeight: '85vh', overflowY: 'auto',
    backgroundColor: '#fff', borderRadius: 8, padding: 24, boxSizing: 'border-box',
};
const editLabelStyle: React.CSSProperties = { display: 'block', marginBottom: 16, fontWeight: 700 };
const editInputStyle: React.CSSProperties = {
    display: 'block', width: '100%', boxSizing: 'border-box', padding: 8, marginTop: 6,
    border: '1px solid #cbd5e1', borderRadius: 4, font: 'inherit',
};
